import { useState, useEffect, useCallback } from 'react';
import { hasPrivilege } from '../../api/token';
import { withLocale } from '../../l10n';
import { GetColumnLiteralSearchProps } from '../../utils/functions';
import { Button, Space, Modal, Input, Select, Switch, Tag, notification } from 'antd';
import { FormOutlined } from "@ant-design/icons";
import DataTable from '../DataTable/index';
import service from "../../services/Service";
import { useGetModelsLookupQuery, useGetTrackerModelsLookupQuery } from '../../services/lookupApi.js';
import './TrackerList.scss';

function TrackerList({ lang }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
    showSingleModal: false,
    showBulkModal: false
  });

  const [data, setData] = useState({
    dataSource: [],
    selectedTrackers: []
  });

  const [form, setForm] = useState({
    api_key_ecomaster: "",
    api_key_kibox: "",
    ecomaster_model_name: "",
    api_location_enabled: false,
    tracker_enabled: false
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: models = [] } = useGetModelsLookupQuery();
  const { data: trackerModels = [] } = useGetTrackerModelsLookupQuery();

  const canEdit = hasPrivilege('tracker_edit');
  const boxCModelsUuids = ["a9a11bdb-e2f6-42cf-9286-c89fb19aa103", "d6a8a417-cc47-4b25-9a6c-4f7160b02a9f"];

  // ===== 3. DERIVADOS (Computados) =====
  const btnDisabled = data.selectedTrackers.length === 0;
  const filteredModels = models
    .filter(model => boxCModelsUuids.includes(model.uuid))
    .map(model => ({ text: model.name, value: model.uuid }));
  const controlModelFilters = trackerModels.map(name => ({ text: name, value: name }));

  // ===== 4. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(p => ({ ...p, [field]: value }));
  const updateData = (field, value) => setData(p => ({ ...p, [field]: value }));
  const updateForm = (field, value) => setForm(p => ({ ...p, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const fetchTrackers = useCallback(async () => {
    updateUI('loading', true);
    try {
      const trackersRes = await service.get("v0/tracker");
      
      if (trackersRes.data.ok === false) {
        notification.error({
          message: trackersRes.data.message
        });
        updateUI('loading', false);
        return;
      }

      updateData('dataSource', trackersRes.data.data || []);
    } catch (error) {
      console.error("Error fetching trackers:", error);
    } finally {
      updateUI('loading', false);
    }
  }, []);

  const openSingleModal = (tracker) => {
    setForm({
      api_key_ecomaster: tracker.api_key_ecomaster || "",
      api_key_kibox: tracker.api_key_kibox || "",
      ecomaster_model_name: tracker.ecomaster_model_name || "",
      api_location_enabled: tracker.api_location_enabled || false,
      tracker_enabled: tracker.tracker_enabled || false
    });
    updateUI('showSingleModal', true);
  };

  const openBulkModal = () => {
    updateUI('showBulkModal', true);
  }

  const closeAllModals = () => {
    setUI({showSingleModal: false, showBulkModal: false});
    updateData('selectedTrackers', []);
    setForm({
      api_key_ecomaster: "",
      api_key_kibox: "",
      ecomaster_model_name: "",
      api_location_enabled: false,
      tracker_enabled: false
    });
  }

  // ===== 6. HANDLERS =====
  const openUpdateModal = () => {
    if (data.selectedTrackers.length === 1) {
      openSingleModal(data.selectedTrackers[0]);
    } else if (data.selectedTrackers.length > 1) {
      openBulkModal();
    }
  };

  const handleSingleComfirm = async () => {
    try {
      updateUI('loading', true);
      await service.put(`boards/${data.selectedTrackers[0].ki_id}`, {
        api_key_ecomaster: form.api_key_ecomaster.trim(),
        api_key_kibox: form.api_key_kibox.trim(),
        api_location_enabled: form.api_location_enabled,
        ecomaster_model_name: form.ecomaster_model_name,
        tracker_enabled: form.tracker_enabled
      });
      await fetchTrackers();
      notification.success({
        message: lang.handle('Actualizado con exito')
      });
    } catch (error) {
      console.error("Error updating keys:", error);
    } finally {
      closeAllModals();
      updateUI('loading', false);
    }
  }

  const handleBulkConfirm = async () => {
    try {
      updateUI('loading', true);

      const payload = data.selectedTrackers.map(tracker => ({
        ki_id: tracker.ki_id,
        tracker_enabled: form.tracker_enabled,
        api_location_enabled: form.api_location_enabled
      }));
  
      const response = await service.put(`boards/updateAllTrackers`, { payload: payload });
      
      if (response?.status === 200 && response.data?.ok) {

        const successCount = response.data.data.count || '0';

        notification.success({
          message: `${successCount} trackers actualizados correctamente`
        });

        await fetchTrackers();

      } else {
        notification.error({
          message: lang.handle('Error en la operación'),
        });
      }

    } catch {
      notification.error({
        message: lang.handle('Error en la operación'),
      });

    } finally {
      closeAllModals();
      updateUI('loading', false);
    }
  }

  // ===== 7. EFFECTS =====
  useEffect(() => {
    fetchTrackers();
  }, [fetchTrackers]);
  
  // ===== 8. CONFIG (columnas, etc) =====
  const columns = [
          { 
            title: lang.handle('Nombre'), 
            dataIndex: 'device_name',
            key: 'device_name',
            sorter: (a, b) => (a.device_name || "").localeCompare(b.device_name || ""),
            ...GetColumnLiteralSearchProps('device_name', lang)
          },
          { 
            title: 'Uuid', 
            dataIndex: 'uuid',
            key: 'uuid',
            sorter: (a, b) => a.uuid.localeCompare(b.uuid),
            ...GetColumnLiteralSearchProps('uuid', lang)
          },
          { 
            title: 'Imei', 
            dataIndex: 'physical_id',
            key: 'physical_id',
            sorter: (a, b) => a.physical_id.localeCompare(b.physical_id),
            ...GetColumnLiteralSearchProps('physical_id', lang)
          },
          { 
            title: lang.handle('Modelo'), 
            dataIndex: 'model_name',
            key: 'model_name',
            sorter: (a, b) => (a.model_name || "").localeCompare(b.model_name || ""),
            filters: filteredModels,
            onFilter: (value, record) => record.model_uuid === value
          },
          { 
            title: lang.handle('Modelo control'), 
            dataIndex: 'ecomaster_model_name',
            key: 'ecomaster_model_name',
            sorter: (a, b) => (a.ecomaster_model_name || "").localeCompare(b.ecomaster_model_name || ""),
            filters: controlModelFilters,
            onFilter: (value, record) => record.ecomaster_model_name === value
          },
          { 
            title: lang.handle('Clave API control'), 
            dataIndex: 'api_key_ecomaster',
            key: 'api_key_ecomaster',
            sorter: (a, b) => (a.api_key_ecomaster || "").localeCompare(b.api_key_ecomaster || ""),
            ...GetColumnLiteralSearchProps('api_key_ecomaster', lang)
          },
          { 
            title: lang.handle('Clave API box'), 
            dataIndex: 'api_key_kibox',
            key: 'api_key_kibox',
            sorter: (a, b) => (a.api_key_kibox || "").localeCompare(b.api_key_kibox || ""),
            ...GetColumnLiteralSearchProps('api_key_kibox', lang)
          },
          {
            title: 'Api_location',
            dataIndex: 'api_location_enabled',
            key: 'api_location_enabled',
            render: (enabled) => (
              enabled
                ? <Tag color="green">{lang.handle('Habilitada')}</Tag>
                : <Tag color="red">{lang.handle('Deshabilitada')}</Tag>
            ),
            filters: [
              { text: lang.handle('Habilitada'), value: true },
              { text: lang.handle('Deshabilitada'), value: false }
            ],
            onFilter: (value, record) => record.api_location_enabled === value,
            sorter: (a, b) => (a.api_location_enabled === b.api_location_enabled ? 0 : a.api_location_enabled ? -1 : 1)
          },
          {
            title: 'Tracker_enabled',
            dataIndex: 'tracker_enabled',
            key: 'tracker_enabled',
            render: (enabled) => (
              enabled
                ? <Tag color="green">{lang.handle('Habilitada')}</Tag>
                : <Tag color="red">{lang.handle('Deshabilitada')}</Tag>
            ),
            filters: [
              { text: lang.handle('Habilitada'), value: true },
              { text: lang.handle('Deshabilitada'), value: false }
            ],
            onFilter: (value, record) => record.tracker_enabled === value,
            sorter: (a, b) => (a.tracker_enabled === b.tracker_enabled ? 0 : a.tracker_enabled ? -1 : 1)
          }
        ];

  const rowSelection = canEdit ? {
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectedRows.length === 0) {
        setData({ ...data, selectedTrackers: [] });
      } else {
        setData({ ...data, selectedTrackers: selectedRows });
      }
    }
  } : null;

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        columns={columns}
        dataSource={data.dataSource}
        rowKey="ki_id"
        pagination={true}
        loading={ui.loading}
        rowSelection={canEdit ? {
          type: 'checkbox',
          selectedRowKeys: data.selectedTrackers.map(t => t.ki_id),
          ...rowSelection,
        } : null}
      />
      {canEdit &&(
        <div className="button-panel">
          <Space>
            <Button
              type="primary"
              icon={<FormOutlined />}
              onClick={openUpdateModal}
              disabled={btnDisabled}
            >
              {lang.handle('Editar tracker')}
            </Button>
          </Space>
        </div>
      )}
      <Modal
        title={lang.handle('Nombre: ') +
           (data.selectedTrackers.length > 0 && data.selectedTrackers[0].device_name ? data.selectedTrackers[0].device_name : '')}
        onCancel={closeAllModals}
        cancelText={lang.handle('Cerrar')}
        onOk={handleSingleComfirm}
        okText={lang.handle('Guardar')}
        open={ui.showSingleModal}
        className="tracker-modal">

        <label>{lang.handle("Clave API control")}:</label>
        <Input
          value={form.api_key_ecomaster}
          onChange={e => updateForm("api_key_ecomaster", e.target.value)}
          placeholder={lang.handle("Clave API control")}
        />

        <label>{lang.handle("Clave API box")}:</label>
        <Input
          value={form.api_key_kibox}
          onChange={e => updateForm("api_key_kibox", e.target.value)}
          placeholder={lang.handle("Clave API box")}
        />

        <label>{lang.handle('Modelo control')}:</label>
        <Select
          style={{ width: '100%' }}
          value={form.ecomaster_model_name || undefined}
          onChange={value => updateForm("ecomaster_model_name", value)}
          placeholder={lang.handle('Modelo control')}
          >
          {trackerModels.map(name => (
            <Select.Option key={name} value={name}>{name}</Select.Option>
          ))}
        </Select>
        
        <div className="switch-container">
          <label>API Location:</label>
          <Switch
            checked={form.api_location_enabled}
            onChange={checked => updateForm("api_location_enabled", checked)}
          />
        </div>
        <div className="switch-container">
          <label>Tracker enabled:</label>
          <Switch
            checked={form.tracker_enabled}
            onChange={checked => updateForm("tracker_enabled", checked)}
          />
        </div>
      </Modal>

      <Modal
        title={lang.handle('Editando') + ` ${data.selectedTrackers.length} ` + lang.handle('trackers') }
        onCancel={closeAllModals}
        cancelText={lang.handle('Cerrar')}
        onOk={handleBulkConfirm}
        okText={lang.handle('Guardar')}
        open={ui.showBulkModal}
        className="tracker-modal">
        
        <div className="switch-container">
          <label>Tracker enabled: </label>
          <Switch
            checked={form.tracker_enabled}
            onChange={checked => updateForm("tracker_enabled", checked)}
          />
        </div>

        <div className="switch-container">
          <label>Api Location: </label>
          <Switch
            checked={form.api_location_enabled}
            onChange={checked => updateForm("api_location_enabled", checked)}
          />
        </div>
      </Modal>
    </div>
  );
}

export default withLocale(TrackerList);
